import { useEffect, useRef, useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface SubscriptionHandler {
  id: string;
  callback: (payload: any) => void;
}

interface TableSubscription {
  table: string;
  event: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  filter?: string;
  handlers: Map<string, SubscriptionHandler>;
}

/**
 * Centralized realtime subscription manager
 * Prevents duplicate subscriptions and manages all realtime updates in one place
 */
class RealtimeManager {
  private static instance: RealtimeManager;
  private subscriptions = new Map<string, TableSubscription>();
  private channel: any = null;
  private isConnected = false;

  private constructor() {}

  static getInstance(): RealtimeManager {
    if (!RealtimeManager.instance) {
      RealtimeManager.instance = new RealtimeManager();
    }
    return RealtimeManager.instance;
  }

  private getSubscriptionKey(table: string, event: string, filter?: string): string {
    return `${table}-${event}-${filter || 'all'}`;
  }

  subscribe(
    table: string,
    event: 'INSERT' | 'UPDATE' | 'DELETE' | '*',
    callback: (payload: any) => void,
    filter?: string
  ): string {
    const handlerId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const subscriptionKey = this.getSubscriptionKey(table, event, filter);

    if (table !== 'global_investment_clock') {
      console.log(`📡 Subscribing to ${table} (${event}) with handler ${handlerId}`);
    }

    // Get or create subscription for this table/event/filter combination
    let subscription = this.subscriptions.get(subscriptionKey);
    
    if (!subscription) {
      subscription = {
        table,
        event,
        filter,
        handlers: new Map()
      };
      this.subscriptions.set(subscriptionKey, subscription);
    }

    // Add handler to this subscription
    subscription.handlers.set(handlerId, { id: handlerId, callback });

    // Setup channel if we don't have one or if it's not connected
    this.ensureChannelSetup();

    return handlerId;
  }

  unsubscribe(handlerId: string): void {
    console.log(`📡 Unsubscribing handler ${handlerId}`);
    
    // Find and remove handler from all subscriptions
    for (const [key, subscription] of this.subscriptions) {
      if (subscription.handlers.has(handlerId)) {
        subscription.handlers.delete(handlerId);
        
        // If no more handlers for this subscription, remove it
        if (subscription.handlers.size === 0) {
          this.subscriptions.delete(key);
          console.log(`📡 Removed subscription ${key} (no more handlers)`);
        }
        break;
      }
    }

    // If no more subscriptions, cleanup channel
    if (this.subscriptions.size === 0) {
      this.cleanup();
    }
  }

  private ensureChannelSetup(): void {
    // Only setup if we don't have a channel or if it's been closed
    if (!this.channel || !this.isConnected) {
      this.setupChannel();
    }
  }

  private setupChannel(): void {
    // Cleanup any existing channel first
    this.cleanup();

    const channelName = `realtime-manager-${Date.now()}`;
    console.log(`📡 Setting up centralized channel: ${channelName}`);
    
    this.channel = supabase.channel(channelName);

    // Add all current subscriptions to the channel
    for (const subscription of this.subscriptions.values()) {
      const config: any = {
        event: subscription.event,
        schema: 'public',
        table: subscription.table
      };

      if (subscription.filter) {
        config.filter = subscription.filter;
      }

      this.channel.on('postgres_changes', config, (payload: any) => {
        if (subscription.table !== 'global_investment_clock') {
          console.log(`📡 Received update for ${subscription.table}:`, payload);
        }
        
        // Call all handlers for this subscription
        for (const handler of subscription.handlers.values()) {
          try {
            handler.callback(payload);
          } catch (error) {
            console.error(`Error in realtime handler ${handler.id}:`, error);
          }
        }
      });
    }

    // Subscribe to the channel only once
    this.channel.subscribe((status: string) => {
      console.log(`📡 Centralized channel status: ${status}`);
      this.isConnected = status === 'SUBSCRIBED';
    });
  }

  private cleanup(): void {
    if (this.channel) {
      console.log('📡 Cleaning up centralized realtime channel');
      supabase.removeChannel(this.channel);
      this.channel = null;
      this.isConnected = false;
    }
  }

  // Public method to force reconnection if needed
  reconnect(): void {
    console.log('📡 Forcing realtime reconnection');
    this.isConnected = false;
    this.cleanup();
    if (this.subscriptions.size > 0) {
      this.setupChannel();
    }
  }

  // Get status for debugging
  getStatus() {
    return {
      isConnected: this.isConnected,
      subscriptionCount: this.subscriptions.size,
      subscriptions: Array.from(this.subscriptions.keys())
    };
  }
}

/**
 * Hook to subscribe to realtime updates through the centralized manager
 */
export const useRealtimeSubscription = (
  table: string,
  event: 'INSERT' | 'UPDATE' | 'DELETE' | '*',
  callback: (payload: any) => void,
  filter?: string,
  enabled: boolean = true
) => {
  const handlerIdRef = useRef<string | null>(null);
  const manager = RealtimeManager.getInstance();

  useEffect(() => {
    if (!enabled) return;

    // Subscribe
    handlerIdRef.current = manager.subscribe(table, event, callback, filter);

    return () => {
      // Unsubscribe
      if (handlerIdRef.current) {
        manager.unsubscribe(handlerIdRef.current);
        handlerIdRef.current = null;
      }
    };
  }, [table, event, filter, enabled, callback]);

  return {
    reconnect: manager.reconnect.bind(manager),
    getStatus: manager.getStatus.bind(manager)
  };
};

// Investment clock realtime hook removed - legacy system deprecated

/**
 * Hook for time progression updates
 */
export const useTimeProgressionRealtime = (callback: (payload: any) => void, enabled: boolean = true) => {
  const { user } = useAuth();
  return useRealtimeSubscription(
    'time_periods', 
    'UPDATE', 
    callback, 
    'is_active=eq.true', 
    enabled && !!user?.id
  );
};

/**
 * Hook for user session updates
 */
export const useUserSessionRealtime = (callback: (payload: any) => void, enabled: boolean = true) => {
  const { user } = useAuth();
  return useRealtimeSubscription(
    'active_user_sessions',
    '*',
    callback,
    undefined,
    enabled && !!user?.id
  );
};

/**
 * Hook for user task completion updates
 */
export const useUserTaskCompletionRealtime = (
  callback: (payload: any) => void,
  locationId?: string,
  enabled: boolean = true
) => {
  const { user } = useAuth();
  return useRealtimeSubscription(
    'player_time_task_completions',
    'INSERT',
    callback,
    user?.id ? `user_id=eq.${user.id}` : undefined,
    enabled && !!user?.id && !!locationId
  );
};

/**
 * Hook for user question response updates
 */
export const useUserQuestionResponseRealtime = (
  callback: (payload: any) => void,
  enabled: boolean = true
) => {
  const { user } = useAuth();
  return useRealtimeSubscription(
    'player_freeworld_answers',
    'INSERT',
    callback,
    user?.id ? `user_id=eq.${user.id}` : undefined,
    enabled && !!user?.id
  );
};